import React, { useState, useEffect, useMemo, FC } from 'react';
import Card from '../Card/Card';
import Const from 'const.json'
import { ButtonDictionaryItem } from 'types/ButtonDictionaryItem';
import GroupButtons from 'components/controls/GroupButtons/GroupButtons';
import ChartBar from 'components/Charts/ChartBar/ChartBar';
import { useYearFromRedux } from 'utils/utilsLogic';
import { useAppSelector } from '../../redux/hooks';
import useApi from 'hooks/useApi';
import { GetPopulation } from 'api/apiService';
import { PopulationData } from 'types/Population';
import WithApi from 'components/ui/WithApi/WithApi';
import { numberWithCommas, pluginsDefinitions, relativeNumber } from 'utils/utils';
import { MediaQuery, PopulationGraphTypeEnum } from 'enums/enums';
import ExportToExcel from 'components/controls/ExportToExcel/ExportToExcel';
import useMediaQuery from 'hooks/useMediaQuery';

const ComplaintsNumberByPopulationGroup: FC = () => {
    const isMobile = useMediaQuery(MediaQuery.Mobile);

    const { period, isContrast } = useAppSelector(state => state.general);
    const { lastYearFromSession } = useYearFromRedux();
    const { callApi, data, loading, error } = useApi<PopulationData[]>(GetPopulation);
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(0);

    const colorDictionary = {
        [lastYearFromSession.toString()]: isContrast ? '#ffc0cb' : "#1172E4",
        [(lastYearFromSession - 1).toString()]: isContrast ? '#add8e6' : "#AED3FF"
    };

    useEffect(() => {
        callApi();
    }, []);

    useEffect(() => {
        setCurrentIndex(0);
    }, [period]);

    useEffect(() => {
        if (data) {
            let headers = [], rows = [];
            headers[0] = `${Const.year}/${data[currentIndex].populationType}`;
            let index = -1;
            rows[index] = [];
            data[currentIndex].populationData.forEach(x => {
                !headers.includes(x.description) && headers.push(x.description);
                if (rows[index].includes(x.year)) {
                    rows[index].push(parseFloat(x.count));
                }
                else {
                    rows[++index] = [x.year.toString(), parseFloat(x.count)]
                }
            })
            setHeaders(headers);
            setRows(rows);
        }
    }, [data, currentIndex]);

    const [ageData, educationData, religionData] = useMemo(() => {
        let _ageData, _educationData, _religionData;
        if (data) {
            _ageData = getData(data, colorDictionary, isContrast, PopulationGraphTypeEnum.Age);
            _educationData = getData(data, colorDictionary, isContrast, PopulationGraphTypeEnum.Education);
            _religionData = getData(data, colorDictionary, isContrast, PopulationGraphTypeEnum.Religion);
        }
        return [_ageData, _educationData, _religionData];
    }, [data, isContrast]);

    const getChartBarYAxisHeader = (label: string) => `${Const.numberOfComplainantsTo}${relativeNumber}\n${Const.perCapitaBy} ${label}`;

    const buttonDictionary: ButtonDictionaryItem[] = [
        { label: Const.age, component: <ChartBar ariaLabel={Const.numberOfComplainantsByPopulationGroups} details={ageData} chartBarYAxisHeader={getChartBarYAxisHeader(Const.age)} chartBarXAxisHeader={Const.age} displayLabels={!isMobile} /> },
        { label: Const.education, component: <ChartBar ariaLabel={Const.numberOfComplainantsByPopulationGroups} details={educationData} chartBarYAxisHeader={Const.percentageofComplaints} chartBarXAxisHeader={Const.educationYears} /> },
        { label: Const.religion, component: <ChartBar ariaLabel={Const.numberOfComplainantsByPopulationGroups} details={religionData} chartBarYAxisHeader={getChartBarYAxisHeader(Const.religion)} chartBarXAxisHeader={Const.religion} /> }
    ];

    return (
        <div>
            <Card
                header={Const.numberOfComplainantsByPopulationGroups}
                components={[
                    <ExportToExcel headers={headers} data={rows} filename={`${Const.numberOfComplainantsByPopulationGroups} - ${Const[PopulationGraphTypeEnum[currentIndex].toLowerCase()]}`} />,
                    <WithApi error={error} data={data} loading={loading}><GroupButtons buttonNames={buttonDictionary} setCurrentIndex={setCurrentIndex} selectedIndex={currentIndex}/></WithApi>]}
            />
        </div>
    );
};

export default ComplaintsNumberByPopulationGroup;

const getData: any = (data: PopulationData[], colorDictionary: Map<string, string>, isContrast: boolean, index: number) => {
    let barLabelData = [], datasets = [];
    data[index].populationData.forEach(x => {
        !barLabelData.includes(x.description) && barLabelData.push(x.description);
        let index = datasets.findIndex(y => y.label == x.year)
        if (index >= 0) {
            datasets[index].data.push(numberWithCommas(parseFloat(x.count)))
        }
        else {
            datasets.push({
                "label": x.year,
                data: [numberWithCommas(parseFloat(x.count))],
                "pointStyle": "circle",
                "backgroundColor": colorDictionary[x.year],
            })
        }
    });

    return {
        barLabelData: barLabelData,
        datasets: datasets,
        plugins: pluginsDefinitions(isContrast),
    };
}